/**
 * useBrowserRecordingSession Hook
 *
 * Integrates the useBrowserRecording hook with backend API for session management
 * and chunked uploads. Handles the complete recording flow:
 * 1. Start session with backend
 * 2. Record audio with real-time visualization
 * 3. Upload chunks as they're recorded
 * 4. Complete session and trigger transcription
 */

import { useState, useCallback, useRef } from "react";
import {
  useBrowserRecording,
  checkBrowserCompatibility,
} from "./useBrowserRecording";
import {
  startBrowserRecordingSession,
  getChunkUploadUrl,
  uploadChunkToS3,
  completeBrowserRecording,
  abortBrowserRecording,
} from "@/services/browserRecording";
import type {
  BrowserRecordingSession,
  CompleteBrowserRecordingResponse,
} from "@/types/browser-recording";
import type { ExpectedSpeakers } from "@/types/reflection";

/**
 * Session state for the recording
 */
export interface RecordingSessionState {
  /** Backend session info */
  session: BrowserRecordingSession | null;
  /** Whether the session is being initialized */
  isInitializing: boolean;
  /** Whether the session is being completed */
  isCompleting: boolean;
  /** Completed reflection info */
  completedRecording: CompleteBrowserRecordingResponse | null;
  /** Session error */
  sessionError: string | null;
}

/**
 * Options for completing the recording
 */
export interface CompleteRecordingOptions {
  title?: string;
  description?: string;
  userContext?: string;
  therapyStyleId?: string;
  expectedSpeakers?: ExpectedSpeakers;
}

const INITIAL_SESSION_STATE: RecordingSessionState = {
  session: null,
  isInitializing: false,
  isCompleting: false,
  completedRecording: null,
  sessionError: null,
};

/**
 * Hook for managing browser recording with backend integration
 */
export function useBrowserRecordingSession() {
  const [sessionState, setSessionState] = useState<RecordingSessionState>(
    INITIAL_SESSION_STATE
  );

  // Track failed chunks for retry
  const failedChunksRef = useRef<Map<number, Blob>>(new Map());
  const sessionIdRef = useRef<string | null>(null);

  /**
   * Upload a chunk to S3 via presigned URL
   * Throws an error if upload fails so the caller can track failed uploads accurately.
   */
  const handleChunkReady = useCallback(
    async (chunk: Blob, chunkNumber: number): Promise<void> => {
      if (!sessionIdRef.current) {
        failedChunksRef.current.set(chunkNumber, chunk);
        throw new Error("No session ID available for chunk upload");
      }

      try {
        // Get presigned URL from backend
        const { upload_url } = await getChunkUploadUrl(sessionIdRef.current, {
          chunk_number: chunkNumber,
          mime_type: chunk.type,
        });

        // Upload chunk directly to S3
        const success = await uploadChunkToS3(upload_url, chunk);

        if (!success) {
          failedChunksRef.current.set(chunkNumber, chunk);
          throw new Error(`Failed to upload chunk ${chunkNumber} to S3`);
        } else {
          // Remove from failed chunks if it was there
          failedChunksRef.current.delete(chunkNumber);
        }
      } catch (error) {
        failedChunksRef.current.set(chunkNumber, chunk);
        throw error;
      }
    },
    []
  );

  // Initialize the browser recording hook with chunk handler
  const browserRecording = useBrowserRecording({
    onChunkReady: handleChunkReady,
    onError: (error) => {
      setSessionState((prev) => ({
        ...prev,
        sessionError: error,
      }));
    },
  });

  /**
   * Start a new recording session
   */
  const startSession = useCallback(async (): Promise<string> => {
    // Check browser compatibility first
    const compatibility = checkBrowserCompatibility();
    if (!compatibility.mediaRecorder || !compatibility.recommendedMimeType) {
      throw new Error("Browser does not support audio recording");
    }

    setSessionState((prev) => ({
      ...prev,
      isInitializing: true,
      sessionError: null,
    }));

    try {
      // Start backend session
      const session = await startBrowserRecordingSession({
        mime_type: compatibility.recommendedMimeType,
        user_agent: navigator.userAgent,
      });

      sessionIdRef.current = session.session_id;
      failedChunksRef.current.clear();

      setSessionState((prev) => ({
        ...prev,
        session,
        isInitializing: false,
      }));

      // Start browser recording
      await browserRecording.startRecording();

      return session.session_id;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to start recording";

      setSessionState((prev) => ({
        ...prev,
        isInitializing: false,
        sessionError: errorMessage,
      }));

      throw error;
    }
  }, [browserRecording]);

  /**
   * Stop recording and complete the session
   */
  const stopAndComplete = useCallback(
    async (
      options: CompleteRecordingOptions = {}
    ): Promise<CompleteBrowserRecordingResponse> => {
      if (!sessionIdRef.current) {
        throw new Error("No active recording session");
      }

      setSessionState((prev) => ({
        ...prev,
        isCompleting: true,
      }));

      try {
        // Stop the browser recording
        const { totalChunks, duration } =
          await browserRecording.stopRecording();

        // Retry any failed chunks
        if (failedChunksRef.current.size > 0) {
          for (const [
            chunkNumber,
            chunk,
          ] of failedChunksRef.current.entries()) {
            try {
              await handleChunkReady(chunk, chunkNumber);
            } catch (error) {
              console.error(`Failed to retry chunk ${chunkNumber}:`, error);
            }
          }
        }

        // Complete the session with backend
        const response = await completeBrowserRecording(sessionIdRef.current, {
          total_chunks: totalChunks,
          total_duration_seconds: duration,
          title: options.title,
          description: options.description,
          user_context: options.userContext,
          therapy_style_id: options.therapyStyleId,
          expected_speakers: options.expectedSpeakers,
        });

        setSessionState((prev) => ({
          ...prev,
          isCompleting: false,
          completedRecording: response,
        }));

        return response;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to complete recording";

        setSessionState((prev) => ({
          ...prev,
          isCompleting: false,
          sessionError: errorMessage,
        }));

        throw error;
      }
    },
    [browserRecording, handleChunkReady]
  );

  /**
   * Cancel the recording session
   */
  const cancelSession = useCallback(async (): Promise<void> => {
    // Cancel browser recording
    await browserRecording.cancelRecording();

    // Abort backend session if exists
    if (sessionIdRef.current) {
      try {
        await abortBrowserRecording(sessionIdRef.current);
      } catch (error) {
        console.error("Failed to abort backend session:", error);
      }
    }

    // Reset state
    sessionIdRef.current = null;
    failedChunksRef.current.clear();
    setSessionState(INITIAL_SESSION_STATE);
  }, [browserRecording]);

  /**
   * Reset the session state
   */
  const resetSession = useCallback(() => {
    browserRecording.reset();
    sessionIdRef.current = null;
    failedChunksRef.current.clear();
    setSessionState(INITIAL_SESSION_STATE);
  }, [browserRecording]);

  return {
    // Browser recording state
    recordingState: browserRecording.state,
    // Session state
    sessionState,
    // Actions
    startSession,
    stopAndComplete,
    cancelSession,
    resetSession,
    pauseRecording: browserRecording.pauseRecording,
    resumeRecording: browserRecording.resumeRecording,
    // Utils
    checkBrowserCompatibility,
  };
}
